import React from "react";
import { Link } from "react-router-dom";
import formatValue from "../../utils/formatValue";

import {
  Container,
  CoverImageContainer,
  PhotosSlider,
  PropertyInfosContainer,
  PriceFavWrapper,
  Price,
  Title,
  Address,
  PropertyDetailsWrapper,
  PropertyDetailWrapper,
  ImageContentContainer,
  NoPhotoImage,
  FavoriteLoadingImage,
  WireframeMediaBlock,
} from "./style";
import { Property } from "../../global/types";
import { FiHeart } from "react-icons/fi";
import { FaHeart } from "react-icons/fa";
import propertyNotFoundImg from "../../assets/components/PropertiesCardList/no-properties-found.png";
import loadingGif from "../../assets/pages/Imovel/loading.gif";
import { BiBath, BiBed, BiCar } from "react-icons/bi";
import useMultiplesFavorites from "../../hooks/useMultipleFavorites";
import { SliderNextArrow, SliderPrevArrow } from "./SliderArrows";
import LazyPropertyImage from "../LazyPropertyImage";
import { HousingProgramCardBadge } from "../HousingProgramBadge";
import { SLICK_LAZY_LOAD_ON_DEMAND } from "../../utils/photosSliderSettings";
import { getHousingProgramFromProperty } from "../../utils/housingProgram";

const SLIDER_BASE_SETTINGS = {
  speed: 500,
  slidesToShow: 1,
  slidesToScroll: 1,
  infinite: false,
  lazyLoad: SLICK_LAZY_LOAD_ON_DEMAND,
  prevArrow: <SliderPrevArrow />,
  nextArrow: <SliderNextArrow />,
};

interface IProps {
  property: Property;
  isInitiallyFavorite: boolean;
  searchParams?: string;
  className?: string;
  onNavigate?: () => void;
  /** Renders neutral blocks instead of loading real images (e.g. style guide wireframes). */
  coverPlaceholder?: boolean;
}

function formatAddress(address: string) {
  return address.length <= 25
    ? address
    : address.substring(0, 25).concat("...");
}

export default function PropertyCard({
  property,
  isInitiallyFavorite,
  searchParams = "",
  className = "",
  onNavigate,
  coverPlaceholder = false,
}: IProps) {
  const validPhotos =
    property.photos?.filter((photo) => photo && (photo.url_l || photo.url_m)) || [];
  const propertyPhotos = validPhotos.slice(0, 3);
  const { address } = property;
  const housingProgram = getHousingProgramFromProperty(property);
  const { handleFavorite, handleRemoveFavorite, isFavorite, loadingFavorite } =
    useMultiplesFavorites(isInitiallyFavorite);

  const getPropertyUrl = () => {
    const baseUrl = `/imovel/${property.id}`;
    if (searchParams) {
      return `${baseUrl}${searchParams.startsWith('?') ? '' : '?'}${searchParams}`;
    }
    return baseUrl;
  };

  function handleFavoriteClick(
    event: React.MouseEvent<SVGElement, MouseEvent>,
    type: string
  ) {
    event.preventDefault();
    event.stopPropagation();
    if (type === "ADD_FAVORITE") {
      handleFavorite(property.id);
    } else {
      handleRemoveFavorite(property.id);
    }
  }

  const renderImageContent = () => {
    if (coverPlaceholder) {
      const declared = property.photos?.filter((p) => p != null).length ?? 0;
      const slideCount = Math.min(3, Math.max(1, declared || 1));
      const galleryEnabled = slideCount > 1;

      return (
        <PhotosSlider {...SLIDER_BASE_SETTINGS} dots={galleryEnabled} arrows={galleryEnabled}>
          {Array.from({ length: slideCount }, (_, index) => (
            <div key={String(index)}>
              <WireframeMediaBlock aria-hidden />
            </div>
          ))}
        </PhotosSlider>
      );
    }

    if (propertyPhotos.length === 0) {
      return (
        <ImageContentContainer>
          <NoPhotoImage
            src={propertyNotFoundImg}
            alt="Imagem não disponível"
            loading="lazy"
          />
        </ImageContentContainer>
      );
    }

    const galleryEnabled = propertyPhotos.length > 1;

    return (
      <PhotosSlider
        {...SLIDER_BASE_SETTINGS}
        dots={galleryEnabled}
        arrows={galleryEnabled}
      >
        {propertyPhotos.map((photo: any, index: number) => (
          <div key={photo.id || index}>
            <LazyPropertyImage
              src={photo.url_m || photo.url_l}
              alt={`${property.title} - Foto ${index + 1}`}
              loading="lazy"
            />
          </div>
        ))}
      </PhotosSlider>
    );
  };

  return (
    <Link
      to={getPropertyUrl()}
      onClick={onNavigate}
      style={{ textDecoration: 'none', color: 'inherit' }}
      className={className}
    >
      <Container data-testid="property-card" data-property-neighborhood={address?.neighborhood}>
        <CoverImageContainer className="cover-image-container">
          {housingProgram && <HousingProgramCardBadge program={housingProgram} />}
          {renderImageContent()}
        </CoverImageContainer>

        <PropertyInfosContainer className="property-info-container-newcomers">
          <PriceFavWrapper>
            <Price className="property-price-newcomers">
              {formatValue(parseFloat(property.rent_value ?? 0))}
            </Price>
            {!loadingFavorite && !isFavorite && (
              <FiHeart
                style={{ color: "hsla(213, 10%, 18%, 1)" }}
                onClick={(event) => handleFavoriteClick(event, "ADD_FAVORITE")}
              />
            )}
            {!loadingFavorite && isFavorite && (
              <FaHeart
                style={{ color: "hsla(338, 90%, 58%, 1)" }}
                onClick={(event) => handleFavoriteClick(event, "REMOVE_FAVORITE")}
              />
            )}
            {loadingFavorite && <FavoriteLoadingImage src={loadingGif} alt="loadingGif" loading="lazy" />}
          </PriceFavWrapper>

          <Title className="property-title-newcomers">{property.title}</Title>
          <Address className="property-address-newcomers">
            {formatAddress(
              `${address?.city || ''} - ${address?.uf || ''} / ${address?.address || ''}`
            )}
          </Address>

          <PropertyDetailsWrapper className="property-details-wrapper-newcomers">
            <PropertyDetailWrapper>
              <BiBed className="property-detail-icon-newcomers" />
              <p className="property-detail-newcomers">{property.rooms}</p>
            </PropertyDetailWrapper>
            <PropertyDetailWrapper>
              <BiBath className="property-detail-icon-newcomers" />
              <p className="property-detail-newcomers">
                {property.bathrooms ?? 0}
              </p>
            </PropertyDetailWrapper>
            <PropertyDetailWrapper>
              <BiCar className="property-detail-icon-newcomers" />
              <p className="property-detail-newcomers">
                {property.garages ?? 0}
              </p>
            </PropertyDetailWrapper>
          </PropertyDetailsWrapper>
        </PropertyInfosContainer>
      </Container>
    </Link>
  );
}